import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Collapse } from 'antd'

import OrderedPageFilter from 'core/application/commons/orderedPageFilter'
import { PagedProductEligibleEmployee } from 'core/application/csiDashboard/csiSearch/getProductEligibleEmployees'
import { ProductClass } from 'core/domain/benefit/productClass'

import Tooltip from 'common/components/dataDisplay/Tooltip'

import { ExtraHeaderContent, Header } from './ProductClassSummaryComponents'

import './ProductClassSummaryStyless.less'

const { Panel } = Collapse

const DEFAULT_ACTIVE_KEY = 1
const DEFAULT_DISABLED_KEY = 0

interface ProductClassProps {
  id: string
  title: string
  extra?: ReactNode
  delete?: DeleteProps
  showComplete?: boolean
  loadDataAfterRender?: boolean
  onComplete?: () => void
  designSummary?: (
    productClass: ProductClass | null,
    productEligibleEmployee: PagedProductEligibleEmployee | null,
  ) => ReactNode
  apiEndpoint: (id: string) => Promise<ProductClass>
  eligibleEmployeesEndpoint?: (filter: OrderedPageFilter, id: string) => Promise<PagedProductEligibleEmployee>
  employeesFilter?: OrderedPageFilter
}

interface DeleteProps {
  disabled?: boolean
  tooltip?: string
  onClick: () => void
}

const ProductClassSummary = (props: ProductClassProps) => {
  const [viewMoreDetails, setViewMoreDetails] = useState(false)
  const [activeKey, setActiveKey] = useState(DEFAULT_DISABLED_KEY)
  const [productClass, setProductClass] = useState<ProductClass | null>(null)
  const [productEligibleEmployee, setProductEligibleEmployee] = useState<PagedProductEligibleEmployee | null>(null)
  const { t: translate } = useTranslation()

  useEffect(() => {
    getEmployeesData()
  }, [props.employeesFilter])

  const getData = async () => {
    getProductClassData()
    getEmployeesData()
  }

  const getProductClassData = async () => {
    setActiveKey(activeKey === DEFAULT_ACTIVE_KEY ? DEFAULT_DISABLED_KEY : DEFAULT_ACTIVE_KEY)
    setViewMoreDetails(!viewMoreDetails)
    if (!productClass) {
      props.apiEndpoint(props.id).then((response: ProductClass) => {
        setProductClass(response)
      })
    }
  }

  const getEmployeesData = async () => {
    if (props.eligibleEmployeesEndpoint && props.employeesFilter) {
      props
        .eligibleEmployeesEndpoint(props.employeesFilter, props.id)
        .then((response: PagedProductEligibleEmployee) => {
          setProductEligibleEmployee(response)
        })
    }
  }

  const completeNowAction = props.showComplete ? (
    <Button
      onClick={() => {
        if (props.onComplete) props.onComplete()
      }}
      type="default"
      className="complete_now_button outlined_button"
    >
      {translate('admin-dashboard.benefit-details.summary.complete-now')}
    </Button>
  ) : null

  const deleteLinkContent = (): ReactNode => {
    if (!props.delete) {
      return null
    }
    if (props.delete.disabled) {
      return (
        <Tooltip title={props.delete.tooltip ?? ''} position="left">
          <h6 className="link delete_class">{translate('admin-dashboard.benefit-details.summary.delete-class')}</h6>
        </Tooltip>
      )
    } else {
      return (
        <h6 onClick={props.delete?.onClick} className="link delete_class">
          {translate('admin-dashboard.benefit-details.summary.delete-class')}
        </h6>
      )
    }
  }

  const viewMoreActions = (
    <>
      <div className="left_actions">{deleteLinkContent()}</div>
      <div className="right_actions">
        <h6
          onClick={() => {
            setViewMoreDetails(false)
            setActiveKey(DEFAULT_DISABLED_KEY)
          }}
          className="link"
        >
          {translate('admin-dashboard.benefit-details.summary.view-less')}
        </h6>
        {completeNowAction}
      </div>
    </>
  )

  const viewLessActions = (
    <>
      <div className="left_actions"></div>
      <div className="right_actions">
        <h6
          onClick={() => {
            getData()
          }}
          className="link"
        >
          {translate('admin-dashboard.benefit-details.summary.view-details')}
        </h6>
      </div>
    </>
  )

  let content = viewMoreDetails ? (
    <div className="class_content">
      {props.designSummary ? props.designSummary(productClass, productEligibleEmployee) : null}
    </div>
  ) : null

  return (
    <div>
      <Collapse
        activeKey={activeKey}
        onChange={(key) => {
          getData()
        }}
      >
        <Panel
          className="class_design"
          key={DEFAULT_ACTIVE_KEY}
          header={<Header title={props.title} />}
          extra={<ExtraHeaderContent content={props.extra} />}
        >
          {content}
        </Panel>
      </Collapse>
      <div className="class_design_footer">
        <div className="actions">{viewMoreDetails ? viewMoreActions : viewLessActions}</div>
      </div>
    </div>
  )
}

export default ProductClassSummary
